import React from 'react';

// Standard markdown components for feed/news content
export const feedMarkdownComponents = {
    p: ({ children }) => (
        <p style={{ margin: '0.35rem 0', lineHeight: '1.6', fontSize: '0.85rem' }}>
            {children}
        </p>
    ),
    ul: ({ children }) => (
        <ul style={{ margin: '0.3rem 0', paddingLeft: '1.2rem' }}>{children}</ul>
    ),
    ol: ({ children }) => (
        <ol style={{ margin: '0.3rem 0', paddingLeft: '1.2rem' }}>{children}</ol>
    ),
    li: ({ children }) => (
        <li style={{ margin: '0.15rem 0', lineHeight: '1.55', fontSize: '0.85rem' }}>
            {children}
        </li>
    ),
    strong: ({ children }) => (
        <strong style={{ fontWeight: '700', color: '#1D1D1F' }}>{children}</strong>
    ),
    h1: ({ children }) => (
        <h1 style={{ fontSize: '1rem', fontWeight: '700', margin: '0.6rem 0 0.3rem', color: '#1D1D1F' }}>
            {children}
        </h1>
    ),
    h2: ({ children }) => (
        <h2 style={{ fontSize: '0.92rem', fontWeight: '700', margin: '0.5rem 0 0.25rem', color: '#1D1D1F' }}>
            {children}
        </h2>
    ),
    h3: ({ children }) => (
        <h3 style={{ fontSize: '0.88rem', fontWeight: '700', margin: '0.4rem 0 0.2rem', color: '#1D1D1F' }}>
            {children}
        </h3>
    ),
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#007AFF', textDecoration: 'none' }}>
            {children}
        </a>
    ),
};

// Report/article markdown components with more spacing
export const reportMarkdownComponents = {
    h1: ({ children }) => (
        <h1 style={{ fontSize: '1.4rem', fontWeight: '800', margin: '1.5rem 0 0.75rem', color: '#1D1D1F', lineHeight: '1.3' }}>
            {children}
        </h1>
    ),
    h2: ({ children }) => (
        <h2 style={{ fontSize: '1.15rem', fontWeight: '700', margin: '1.25rem 0 0.5rem', color: '#1D1D1F', lineHeight: '1.3', borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: '0.4rem' }}>
            {children}
        </h2>
    ),
    h3: ({ children }) => (
        <h3 style={{ fontSize: '1.02rem', fontWeight: '700', margin: '1rem 0 0.4rem', color: '#1D1D1F' }}>
            {children}
        </h3>
    ),
    p: ({ children }) => (
        <p style={{ margin: '0.5rem 0', lineHeight: '1.7' }}>{children}</p>
    ),
    ul: ({ children }) => (
        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ul>
    ),
    ol: ({ children }) => (
        <ol style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ol>
    ),
    li: ({ children }) => (
        <li style={{ margin: '0.3rem 0', lineHeight: '1.6' }}>{children}</li>
    ),
    strong: ({ children }) => (
        <strong style={{ fontWeight: '700', color: '#1D1D1F' }}>{children}</strong>
    ),
    blockquote: ({ children }) => (
        <blockquote style={{ margin: '0.75rem 0', paddingLeft: '1rem', borderLeft: '3px solid #007AFF', color: '#555', fontStyle: 'italic' }}>
            {children}
        </blockquote>
    ),
    hr: () => (
        <hr style={{ border: 'none', borderTop: '1px solid rgba(0,0,0,0.08)', margin: '1rem 0' }} />
    ),
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#007AFF', textDecoration: 'none', fontWeight: '500' }}>
            {children}
        </a>
    ),
    code: ({ children }) => (
        <code style={{ backgroundColor: 'rgba(0,0,0,0.04)', padding: '2px 6px', borderRadius: '4px', fontSize: '0.88em', fontFamily: 'SF Mono, Menlo, monospace' }}>
            {children}
        </code>
    ),
    table: ({ children }) => (
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0.75rem 0', fontSize: '0.88rem' }}>
            {children}
        </table>
    ),
    thead: ({ children }) => (
        <thead style={{ borderBottom: '2px solid rgba(0,0,0,0.1)' }}>{children}</thead>
    ),
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => (
        <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{children}</tr>
    ),
    th: ({ children }) => (
        <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: '700', color: '#1D1D1F', fontSize: '0.82rem' }}>
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td style={{ padding: '6px 10px', color: '#333' }}>{children}</td>
    ),
};

// Compact markdown for AI summary boxes
export const compactMarkdownComponents = {
    h1: ({ children }) => (
        <h1 style={{ fontSize: '1.2rem', fontWeight: '800', margin: '1rem 0 0.5rem', color: '#1D1D1F' }}>
            {children}
        </h1>
    ),
    h2: ({ children }) => (
        <h2 style={{ fontSize: '1.05rem', fontWeight: '700', margin: '0.8rem 0 0.4rem', color: '#1D1D1F', borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: '0.3rem' }}>
            {children}
        </h2>
    ),
    h3: ({ children }) => (
        <h3 style={{ fontSize: '0.95rem', fontWeight: '700', margin: '0.6rem 0 0.3rem', color: '#1D1D1F' }}>
            {children}
        </h3>
    ),
    p: ({ children }) => (
        <p style={{ margin: '0.4rem 0', lineHeight: '1.65', fontSize: '0.88rem' }}>{children}</p>
    ),
    ul: ({ children }) => (
        <ul style={{ margin: '0.4rem 0', paddingLeft: '1.3rem' }}>{children}</ul>
    ),
    ol: ({ children }) => (
        <ol style={{ margin: '0.4rem 0', paddingLeft: '1.3rem' }}>{children}</ol>
    ),
    li: ({ children }) => (
        <li style={{ margin: '0.2rem 0', lineHeight: '1.55', fontSize: '0.88rem' }}>{children}</li>
    ),
    strong: ({ children }) => (
        <strong style={{ fontWeight: '700', color: '#1D1D1F' }}>{children}</strong>
    ),
    blockquote: ({ children }) => (
        <blockquote style={{ margin: '0.5rem 0', paddingLeft: '0.8rem', borderLeft: '3px solid #5856D6', color: '#555', fontStyle: 'italic' }}>
            {children}
        </blockquote>
    ),
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#007AFF', textDecoration: 'none', fontWeight: '500' }}>
            {children}
        </a>
    ),
};

export default {
    feedMarkdownComponents,
    reportMarkdownComponents,
    compactMarkdownComponents,
};
